import { ReactElement, useState } from 'react'
import { SelectTags } from 'components/v2/SelectTags'
import { IInputProps } from 'components/Input'
import { GTCompaniesV2Response, GTCompanySuggestionsResponse, GTCompanyV2Vertical } from 'gql/types.generated'
import { GET_COMPANIES_SUGGESTIONS, GET_COMPANIES_V2 } from 'features/Companies/queries-legacy'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useDebouncedQuery } from 'components/v2/Autocomplete'

const sanitizeCompaniesV2Items = (
  items: GTCompaniesV2Response['items'],
  verticals?: GTCompanyV2Vertical[],
  selectedCompaniesIds?: string[]
): ICompanyAutocomplete[] => {
  return items
    .filter((company) => {
      if (verticals && !verticals.includes(company.vertical)) {
        return false
      }
      if (selectedCompaniesIds && selectedCompaniesIds.includes(company.id)) {
        return false
      }
      return true
    })
    .map(({ id, name, vertical, policy }) => ({
      id,
      name,
      vertical,
      policy: policy || undefined
    }))
}

export interface ICompanyAutocomplete {
  id: string
  name: string
  vertical?: string
  policy?: {
    id: string
    name: string
  }
}
interface IMultiCompanyAutocompleteProps extends Omit<IInputProps<HTMLInputElement>, 'values' | 'onChange'> {
  values: ICompanyAutocomplete[]
  onChange: (values: ICompanyAutocomplete[]) => void
  verticals?: GTCompanyV2Vertical[]
  disabled?: boolean
  filterFn?: (companies: ICompanyAutocomplete[]) => ICompanyAutocomplete[]
  useCompanyPermission?: boolean
  readOnlyValues?: ICompanyAutocomplete[]
}

export const MultiCompanyAutocomplete = ({
  values,
  verticals,
  disabled,
  onChange,
  filterFn,
  useCompanyPermission,
  readOnlyValues,
  ...props
}: IMultiCompanyAutocompleteProps): ReactElement => {
  const selectedCompaniesIds = values.map(({ id }) => id)
  const [pattern, setPattern] = useState('')
  const [
    getCompaniesAutocomplete,
    { data: companiesAutocompleteData, loading: companiesAutocomplateLoading, error: companiesAutocompleteError }
  ] = useLazyQuery<{ companySuggestions: GTCompanySuggestionsResponse }>(GET_COMPANIES_SUGGESTIONS)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getCompaniesAutocomplete,
    pattern,
    extraInputs: {
      verticals
    },
    skip: disabled || useCompanyPermission
  })
  const {
    data: companiesV2Data,
    loading: companiesV2Loading,
    error: companiesV2Error
  } = useQuery<{ companiesV2: GTCompaniesV2Response }>(GET_COMPANIES_V2, {
    fetchPolicy: 'network-only',
    variables: {
      limit: 1000,
      page: 1
    },
    skip: disabled || !useCompanyPermission
  })

  const shownItems = useCompanyPermission
    ? sanitizeCompaniesV2Items(companiesV2Data?.companiesV2.items || [], verticals, selectedCompaniesIds)
    : companiesAutocompleteData?.companySuggestions.items.filter((company: ICompanyAutocomplete) => {
        return selectedCompaniesIds.indexOf(company.id) === -1
      }) || []

  const handleChange = (values: ICompanyAutocomplete[], pattern = ''): void => {
    onChange(values)
    setPattern(pattern)
  }

  const loading = companiesV2Loading || companiesAutocomplateLoading || isDebouncing
  const error = companiesV2Error || companiesAutocompleteError

  return (
    <SelectTags
      {...props}
      disabled={disabled}
      values={values}
      items={filterFn ? filterFn(shownItems) : shownItems}
      getTagLabel={(company: ICompanyAutocomplete): string => company.name}
      loading={loading}
      error={error}
      onChange={handleChange}
      readOnlyValues={readOnlyValues}
      shouldShowItems={shouldShowItems}
    />
  )
}
